import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { Flex } from "common/components/flex/flex";
import { useDispatch } from "react-redux";
import Candidate from "redux/models/candidate";
import UDF from "redux/models/udf";
import { setCandidateUdf } from "redux/slices/udfSlice";
import K from "utilities/constants";
import { isEmpty } from "lodash";
import { PipelineStageTracker } from "./pipelineStageTracker/pipelineStageTracker";
import { ContactDetails } from "./contactDetails/contactDetails";
import { ProfessionalInfo } from "./professionalInfo/professionalInfo";
import { UserDefines } from "./userDefineds/userDefines";
import { StatusDetails } from "./statusDetails/statusDetails";
import { useIsAssignedToJob } from "../../hooks/useIsAssignedToJob";
import styles from "./candidateDetailsContainer.module.scss";

const { StageStatus } = K.CandidatePipeline;

const shouldRefetchPipelineOnDateChange = (stageStatus) => {
  return [StageStatus.Completed, StageStatus.Skipped].includes(stageStatus);
};

export const CandidateDetailsContainer = ({
  clientId,
  candidateActivity,
  setCandidateActivity,
  jobDetails,
  onStatusChanged,
  onCandidateChanged,
  onStatusHistoryChange,
  hideContactDetails = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const [activeStage, setActiveStage] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const isAssignedToJob = useIsAssignedToJob(jobDetails);

  const fetchPipeline = useCallback(
    async (updateActiveStage = true) => {
      try {
        const { data } = await Candidate.v2.getPipeline(candidateActivity.id);
        const current = data.find((s) => s.status === StageStatus.Current);
        setPipeline(data);

        if (updateActiveStage) {
          setActiveStage(current ?? null);
        }
      } catch {
        message.error("Failed to fetch candidate pipeline");
      }
    },
    [candidateActivity.id],
  );

  const handleStageClick = useCallback((stage) => {
    setActiveStage(stage);
  }, []);

  const handleStatusUpdated = useCallback(
    async (updatedCandidate, declinedCandidates) => {
      await fetchPipeline();
      setCandidateActivity((prev) => ({ ...prev, ...updatedCandidate }));
      onStatusChanged?.(updatedCandidate, declinedCandidates);
    },
    [fetchPipeline, onStatusChanged, setCandidateActivity],
  );

  const onDateChanged = (dateField, newValue, jobEntry) => {
    setActiveStage((prev) => ({
      ...prev,
      dateFields: prev.dateFields.map((d) =>
        d.fieldName === dateField ? { ...d, value: newValue } : d,
      ),
    }));

    setCandidateActivity((prev) => ({ ...prev, ...jobEntry }));

    if (shouldRefetchPipelineOnDateChange(activeStage.status)) {
      fetchPipeline(false);
    }
  };

  const getCandidateUdfs = useCallback(
    async (entityKeyId = null, loading = true) => {
      const candidateUdfs = await UDF.getEditUdfFields(
        {
          clientId,
          entityId: K.UDF.Entities.Candidate.id,
          entityKey: K.UDF.Entities.Candidate.key,
          entityKeyId,
          isVisible: 1,
        },
        loading,
      );
      dispatch(setCandidateUdf(candidateUdfs));
      return candidateUdfs;
    },
    [clientId, dispatch],
  );

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  return (
    <Flex direction="column" gap={8}>
      {!isEmpty(pipeline) && (
        <PipelineStageTracker
          stages={pipeline}
          activeStageKey={activeStage?.key ?? null}
          onStageClick={handleStageClick}
        />
      )}
      <div style={{ height: 500 }} className="scrollable-container">
        <div className={styles.container}>
          {!isEmpty(pipeline) && (
            <StatusDetails
              key={candidateActivity.id}
              clientId={clientId}
              jobDetails={jobDetails}
              candidateJobEntry={candidateActivity}
              setCandidateJobEntry={setCandidateActivity}
              isAssignedToJob={isAssignedToJob}
              stageStatus={activeStage?.status ?? null}
              stageDateFields={activeStage?.dateFields ?? []}
              onStatusUpdated={handleStatusUpdated}
              onStatusHistoryChange={onStatusHistoryChange}
              onDateChanged={onDateChanged}
              styles={props.styles}
            />
          )}
          <div className={styles.sectionsGrid}>
            {!hideContactDetails && (
              <div className={styles.sectionItem}>
                <ContactDetails
                  candidateJobEntry={candidateActivity}
                  setCandidateJobEntry={setCandidateActivity}
                  onCandidateChanged={onCandidateChanged}
                  isAssignedToJob={isAssignedToJob}
                />
              </div>
            )}
            <div className={styles.sectionItem}>
              <ProfessionalInfo
                candidateJobEntry={candidateActivity}
                setCandidateJobEntry={setCandidateActivity}
                onCandidateChanged={onCandidateChanged}
                isAssignedToJob={isAssignedToJob}
              />
            </div>
            <div
              className={`${styles.sectionItem} ${
                !hideContactDetails ? styles.sectionItem_fullWidth : ""
              }`}
            >
              <UserDefines
                candidateJobEntry={candidateActivity}
                isAssignedToJob={isAssignedToJob}
                getCandidateUdfs={getCandidateUdfs}
              />
            </div>
          </div>
        </div>
      </div>
    </Flex>
  );
};
